import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { hasManagerAccess } from '@/api/user';
import { SessionLoader } from '@/components/SessionLoader';

interface AuthenticatedRouteProps {
  children: ReactNode;
  /** When true, this is the onboarding route: allow access without onboarding, redirect to / if already completed. */
  isOnboardingPage?: boolean;
}

export const AuthenticatedRoute = ({ children, isOnboardingPage = false }: AuthenticatedRouteProps) => {
  const { user, isSessionLoading, isColorReady } = useAuth();
  const location = useLocation();

  if (isSessionLoading) {
    return <SessionLoader />;
  }

  if (!user) {
    return (
      <Navigate
        to='/welcome'
        state={{ from: location.pathname }}
        replace
      />
    );
  }
  if (!isColorReady) {
    return <SessionLoader />;
  }

  const canSkipOnBoarding = hasManagerAccess(user) ? user.onboardingCompletedAt != null : user.languageSlug != null;

  if (isOnboardingPage) {
    if (canSkipOnBoarding) {
      return (
        <Navigate
          to='/'
          state={{ arrivalSource: 'onboarding' }}
          replace
        />
      );
    }
    return <>{children}</>;
  }

  if (!canSkipOnBoarding) {
    return (
      <Navigate
        to='/onboarding'
        replace
      />
    );
  }

  return <>{children}</>;
};
